import React, { useEffect, useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  createTheme,
  ThemeProvider,
  Grid,
  Container,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AppsIcon from "@mui/icons-material/Apps";
import { useLocation, useParams } from "react-router";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import IconButton from "@mui/material/IconButton";

import { updateHistory, setHistoryLoaded } from "../store/ducks/duckLayout";
import { openSidebar } from "../store/ducks/duckSidebar";
import { shallowEqual } from "../utils/comparer";
import { useStylesLayout } from "../styles/Layout";
import { log } from "../utils/logger";
import { actionToLog } from "../store/ducks/duckAudit";
import { setReunionData } from "../store/ducks/duckLayoutCustom";

import PathWay from "./PathWay";
import { useAppInsights } from "./AppInsightsProvider";

const TOTAL = "TOTAL";
const NORMAL = "normal";
const TOTAL_DEFAULT_FILTERS = new Set(["COUNTRY", "REGION"]);
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
const isTotalOption = (option) =>
  ((option?.value || "").trim().toUpperCase() === TOTAL ||
    (option?.label || "").trim().toUpperCase().startsWith("TOD")) &&
  option;

export const heightIFrameControl = 60;
const theme = createTheme({
  palette: {
    primary: {
      main: "#fff",
    },
    type: "dark",
    action: {
      selected: "#fff",
      hover: "#fff",
      disabled: "#fff",
    },
    background: {
      default: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#fff",
      disabled: "#fff",
    },
  },
  overrides: {
    MuiMenuItem: {
      root: {
        "&$selected": {
          backgroundColor: "rgba(76, 175, 80, 0.6)",
          fontWeight: "bolder",
        },
        color: "rgba(0, 0, 0, 0.87)",
        backgroundColor: "#fff",
      },
    },
    MuiSelect: {
      root: {
        borderColor: "white",
        color: "white",
      },
      outlined: {
        borderColor: "white",
        color: "white",
      },
      icon: {
        color: "white",
      },
    },
  },
});

/**
 * @typedef FilterValue
 * @type {object}
 * @property {string} filterValueParentId - an filterValueParentId.
 * @property {string} stringToMatchParent - string to macth in URL or Histoy of parent.
 * @property {string} parentValue - Value of Parent
 * @property {string} value - Value of Parent
 */

/**
 * @typedef Filter
 * @type {object}
 * @property {FilterValue[]} filterValues filtervales of filter.
 * @property {string} stringToMatch - string to macth in URL or History.
 * @property {string} name - Name  of Filter
 */
/**
 * @typedef Props
 * @type {object}
 * @property {Filter[]} filters - an Filters from API.
 */

/**
 *
 * @param {Props} props
 * @returns
 */
const IFrameControl = ({ filters, setParam, isCustom, setRenderMenu }) => {
  const classes = useStylesLayout();
  const dispatch = useDispatch();
  const [lastUpdated, setLastUpdated] = useState({});
  const { history, historyLoaded } = useSelector((state) => state.layout);
  const { reunionSelected } = useSelector((state) => state.layoutCustom);
  const paths = useSelector((state) => state.sidebar.paths);
  const location = useLocation();
  const { id } = useParams();

  const appInsights = useAppInsights();

  const renderCustomPRE = (_value) => {
    const reunion = `${reunionSelected.substr(0, 3)} ${reunionSelected.substr(
      3,
      reunionSelected.lenght,
    )}`;

    return <>{reunion}</>;
  };

  useEffect(() => {
    let historyDefault = {};

    if (history && filters) {
      filters.forEach((filter) => {
        const { filterValues, stringToMatch, name } = filter;
        /**
         * @type {FilterValue}
         */
        let defaultValue = filterValues.find((x) => x.default)?.value;
        const mustDefaultToTotal = TOTAL_DEFAULT_FILTERS.has((name || "").toUpperCase());
        const totalOption = filterValues.find((x) => isTotalOption(x))?.value;

        defaultValue = defaultValue?.trim();
        if (mustDefaultToTotal && totalOption) {
          defaultValue = totalOption;
        }
        if (!defaultValue && filterValues && filterValues.length > 0) {
          defaultValue = filterValues[0]?.value;
        }
        /**
         * @type {FilterValue}
         */
        let filterValueSelected = filterValues.find((fv) => {
          return (
            fv.filterValueParentId &&
            fv.value === history[stringToMatch] &&
            history[fv.stringToMatchParent] === fv.parentValue
          );
        });

        if (!filterValueSelected) {
          filterValueSelected = filterValues.find((fv) => {
            return (
              /**fv.filterValueParentId && */ fv.value === history[stringToMatch]
              //&& history[fv.stringToMatchParent] === fv.parentValue
            );
          });
        }
        if (name === "Country" && Boolean(filterValueSelected)) {
          let validChilds = filterValues
            .filter(
              (x) => x && filterValueSelected && x.parentValue === history[x.stringToMatchParent],
            )
            .map((x) => x.value);

          let valueSelected = filterValueSelected?.value;
          let parentValueSelected = history[filterValueSelected.stringToMatchParent];

          if (
            Boolean(valueSelected) &&
            !validChilds.includes(valueSelected) &&
            valueSelected !== TOTAL
          ) {
            historyDefault = {
              ...historyDefault,
              [stringToMatch]: parentValueSelected === "TOTAL" ? valueSelected : defaultValue,
            };
          }
        }

        //Si el valor no está en el el valueSelected.history[stringToMatchParent]

        const hasValueInHistory = hasOwn(history, stringToMatch);
        let valueInHistory = hasValueInHistory ? history[stringToMatch] : null;
        //en este caso es porque el filtro tiene el mismo nombre pero varian los valores;
        //por ejemplo mes en formato letra: 'ENE' y luego mes en formato numero: '01';
        let existsInFilterValues = filterValues.some((fv) => fv.value === valueInHistory);

        if (!hasValueInHistory || !existsInFilterValues) {
          historyDefault = {
            ...historyDefault,
            [stringToMatch]: defaultValue,
          };
        }
      });
    }

    if (
      Object.keys(historyDefault).length > 0 &&
      !shallowEqual({ ...history, ...historyDefault }, history)
    ) {
      setLastUpdated({ ...historyDefault });
      log("UpdateHistory, secondUseEffect:", { ...historyDefault });
      dispatch(updateHistory({ ...historyDefault }));
      setLastUpdated((prev) => ({ ...prev, ...historyDefault }));
    } else {
      log("último cambio igual al anterior");
      dispatch(setHistoryLoaded());
    }
  }, [dispatch, history, filters, lastUpdated, setLastUpdated]);

  const handleClickMenuItem =
    (val = null) =>
    (e) => {
      e.preventDefault();
      if (val) {
        dispatch(setReunionData(val.replace(" ", "")));
      }
    };

  /**
   * 
   * @param {Filter} prop 

   */
  const handleParam = (prop, event) => {
    if (prop.type.toLowerCase() === NORMAL) {
      setParam(prop.parameter, event.target.value);
    } else {
      dispatch(updateHistory({ [prop.stringToMatch]: event.target.value }));
    }

    if (Object.keys(history).length > 0 && historyLoaded && localStorage.getItem("Email") != "") {
      appInsights.setAuthenticatedUserContext(localStorage.getItem("Email"));
      appInsights.trackEvent({
        name: "FilterSelected",
        properties: { filters: history, filterId: prop.id },
      });

      dispatch(
        actionToLog({
          action: "SELECTION",
          pathMenu: paths && paths[id] ? paths[id] : location.pathname,
          resource: "Filter",
          resourceId: prop.id,
          details: JSON.stringify({
            ...history,
            [prop.stringToMatch]: event.target.value,
            isCustom,
          }),
          category: "FILTER",
        }),
      );
    }
  };

  const handleLenghtOfText = (text) => {
    return text.length > 20 ? text.slice(0, 20) + ".." : text;
  };

  const toggleFullScreen = (elem) => {
    if (
      (document.fullScreenElement !== undefined && document.fullScreenElement === null) ||
      (document.msFullscreenElement !== undefined && document.msFullscreenElement === null) ||
      (document.mozFullScreen !== undefined && !document.mozFullScreen) ||
      (document.webkitIsFullScreen !== undefined && !document.webkitIsFullScreen)
    ) {
      if (elem.requestFullScreen) {
        elem.requestFullScreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullScreen) {
        elem.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    dispatch(openSidebar());
  };

  let gridControl = [];
  let gridRight = [];

  useEffect(() => {
    if (isCustom !== undefined) setRenderMenu(gridControl.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridControl]);

  if (filters) {
    for (let index = 1; index < 13; index++) {
      let filterInPosition = filters ? filters.find((x) => x.positionNumber === index) : null;
      let el;

      if (filterInPosition) {
        let defaultValue = filterInPosition.filterValues.find((x) => x.default);
        const mustDefaultToTotal = TOTAL_DEFAULT_FILTERS.has(
          (filterInPosition?.name || "").toUpperCase(),
        );
        const totalOption = filterInPosition.filterValues.find((x) => isTotalOption(x));

        if (mustDefaultToTotal && totalOption) {
          defaultValue = totalOption;
        }

        let value = (history && history[filterInPosition.stringToMatch]) ?? defaultValue?.value;

        if (history) {
          let valueSelected = filterInPosition.filterValues.find((fv) => {
            return (
              fv.filterValueParentId &&
              fv.value === history[filterInPosition.stringToMatch] &&
              history[fv.stringToMatchParent] === fv.parentValue
            );
          });

          if (valueSelected) {
            if (
              history[valueSelected.stringToMatchParent] &&
              history[valueSelected.stringToMatchParent] !== valueSelected.parentValue &&
              history[valueSelected.stringToMatchParent] !== TOTAL
            ) {
              value = defaultValue?.value;
            }
          }
        }
        //en este caso es porque el filtro tiene el mismo nombre pero varian los valores
        //por ejemplo mes en formato letra: 'ENE' y luego mes en formato numero: '01'
        let existsInFilterValues = filterInPosition.filterValues.some((fv) => fv.value === value);

        if (!existsInFilterValues) {
          value = defaultValue ? defaultValue?.value : value;
        }

        //Si no tiene valor Default le agrego el primero
        if (!value && filterInPosition.filterValues && filterInPosition.filterValues.length > 0) {
          value = filterInPosition.filterValues[0]?.value;
        }

        if (
          history &&
          history[filterInPosition.stringToMatch] &&
          filterInPosition.name === "Month" &&
          defaultValue
        ) {
          const newMonthValue = filterInPosition.filterValues
            .filter((fv) => fv.parentValue === history[fv.stringToMatchParent])
            .sort((a, b) => (a.order < b.order ? 1 : -1))[0]?.value;

          if (
            newMonthValue &&
            !filterInPosition.filterValues.some(
              (fv) =>
                fv.value === history[filterInPosition.stringToMatch] &&
                fv.parentValue === history[fv.stringToMatchParent],
            )
          ) {
            dispatch(updateHistory({ ...history, MONTH: newMonthValue }));
          }
        }

        value = value?.trim();
        el = (
          <Grid
            key={`filter-${filterInPosition.name}-${index}`}
            item
            spacing={5}
            style={{
              marginLeft: 20,
            }}
          >
            <FormControl
              key={`form-control-${filterInPosition.name}-${index}`}
              fullWidth
              margin="dense"
              variant="outlined"
            >
              <InputLabel
                key={`input-label-${filterInPosition.name}-${index}`}
                id={`inputLabel-${filterInPosition.name}`}
              >
                {handleLenghtOfText(filterInPosition.name)}
              </InputLabel>
              <Select
                key={`select-${filterInPosition.name}-${index}`}
                autoWidth
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: "white",
                      color: "rgba(0, 0, 0, 0.87)",
                    },
                  },
                  MenuListProps: {
                    style: {
                      color: "rgba(0, 0, 0, 0.87)",
                    },
                  },
                }}
                id={`inputLabel-${filterInPosition.name}`}
                label={filterInPosition.name}
                labelId={filterInPosition.name}
                margin="dense"
                {...(isCustom && filterInPosition.stringToMatch === "REUNION" && reunionSelected
                  ? { renderValue: renderCustomPRE }
                  : {})}
                style={{ minHeight: 0, height: 26, color: "white !important" }}
                value={value}
                onChange={(e) => handleParam(filterInPosition, e)}
              >
                {filterInPosition.filterValues
                  .sort((a, b) => (a.order > b.order ? 1 : -1))
                  .map((fv, i) => {
                    let classNameMenuItem =
                      reunionSelected !== null && reunionSelected !== fv.label.replace(" ", "")
                        ? classes.menuItemCustomReset
                        : "";

                    if (!fv.filterValueParentId) {
                      //Si no tiene este valor, es público
                      return (
                        <MenuItem
                          key={`${i}-${fv.label}-${i}`}
                          aria-valuetext={fv.label}
                          value={fv.value}
                          style={{ color: "rgba(0, 0, 0, 0.87)" }}
                          {...(isCustom && filterInPosition.stringToMatch === "REUNION"
                            ? {
                                onClick: handleClickMenuItem(fv.label),
                                className: classNameMenuItem,
                              }
                            : {})}
                        >
                          {fv.label}
                        </MenuItem>
                      );
                    } else {
                      if (
                        history &&
                        history[fv.stringToMatchParent] &&
                        history[fv.stringToMatchParent] === fv.parentValue
                      ) {
                        return (
                          <MenuItem
                            key={`${i}-${fv.label}-${i}`}
                            aria-valuetext={fv.label}
                            value={fv.value}
                            style={{ color: "rgba(0, 0, 0, 0.87)" }}
                            {...(isCustom && filterInPosition.stringToMatch === "REUNION"
                              ? {
                                  onClick: handleClickMenuItem(fv.label),
                                  className: classNameMenuItem,
                                }
                              : {})}
                          >
                            {fv.label}
                          </MenuItem>
                        );
                      } else if (
                        history &&
                        history[fv.stringToMatchParent] &&
                        history[fv.stringToMatchParent] === TOTAL
                      ) {
                        return (
                          <MenuItem
                            key={`${i}-${fv.label}-${i}`}
                            aria-valuetext={fv.label}
                            value={fv.value}
                            style={{ color: "rgba(0, 0, 0, 0.87)" }}
                            {...(isCustom && filterInPosition.stringToMatch === "REUNION"
                              ? {
                                  onClick: handleClickMenuItem(fv.label),
                                  className: classNameMenuItem,
                                }
                              : {})}
                          >
                            {fv.label}
                          </MenuItem>
                        );
                      }
                    }

                    return null;
                  })}
              </Select>
            </FormControl>
          </Grid>
        );
        gridRight.push(el);
      }
    }
  }

  gridControl.unshift(
    <Grid
      key={`Breadcrumb-Helper`}
      item
      xs
      sx={{
        display: "flex",
        alignItems: "center",
        minWidth: 0,
      }}
    >
      <PathWay isCustom={isCustom} />
    </Grid>,
  );

  let container = (
    <Grid key={`Menu-Button`} item xs="auto" sx={{ pr: 1 }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <IconButton
            aria-label="Open sidebar"
            color="inherit"
            sx={{
              backgroundColor: "transparent",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
              },
            }}
            onClick={handleClick}
          >
            <AppsIcon className={classes.appIcon} />
          </IconButton>
        </Grid>
        <Grid item>
          <img
            alt="logo"
            className={classes.logoIframeControl}
            src={`${process.env.PUBLIC_URL}/ReportsNavbar.png`}
          />
        </Grid>
      </Grid>
    </Grid>
  );
  let fullScreen = (
    <Grid item xs="auto">
      <Tooltip title="FullScreen">
        <IconButton
          aria-label="FullScreen"
          color="inherit"
          style={{
            height: 40,
          }}
          onClick={() => toggleFullScreen(document.body)}
        >
          <FullscreenIcon />
        </IconButton>
      </Tooltip>
    </Grid>
  );

  let group = (
    <Grid
      item
      container
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
      xs="auto"
      sx={{ ml: "auto", flexShrink: 0 }}
    >
      {gridRight}
      {fullScreen}
    </Grid>
  );

  gridControl.push(group);
  gridControl.unshift(container);
  gridControl.push();

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" style={{ backgroundColor: "rgb(2, 31, 71)" }}>
        <Grid
          container
          wrap="nowrap"
          alignItems="center"
          style={{ width: "100%", minHeight: heightIFrameControl }}
        >
          {gridControl}
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default React.memo(IFrameControl);
